import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { companyInfo, footerContent, navLinks } from '../data/mock';
import { ArrowUp, Linkedin, Twitter, Github } from 'lucide-react';

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLinkClick = (link, e) => {
    e.preventDefault();
    
    if (link.isRoute) {
      // Navigate to route (like Solutions)
      navigate(link.href);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Handle hash navigation
      if (location.pathname !== '/') {
        // If not on home page, navigate to home first then scroll
        navigate('/');
        setTimeout(() => {
          const element = document.querySelector(link.href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        // Already on home page, just scroll
        const element = document.querySelector(link.href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  return (
    <footer className="footer">
      <div className="container">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <a href="/" className="logo text-2xl">
              {companyInfo.name.slice(0, 2)}<span>{companyInfo.name.slice(2)}</span>
            </a>
            <p className="mt-2 text-sm text-[var(--text-muted)]">
              {companyInfo.fullName}
            </p>
            <p className="mt-4 text-[var(--text-secondary)] max-w-md">
              {companyInfo.description}
            </p>
            <p className="mt-4 font-mono text-[var(--accent-primary)] text-sm">
              {footerContent.tagline}
            </p>
          </div>
          
          {/* Quick Links */}
          <div>
            <h4 className="font-mono font-semibold mb-4 text-sm text-[var(--text-muted)] uppercase tracking-wider">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.label}>
                  <a 
                    href={link.href}
                    onClick={(e) => handleLinkClick(link, e)}
                    className="text-[var(--text-secondary)] hover:text-[var(--accent-primary)] transition-colors text-sm cursor-pointer"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Contact */}
          <div>
            <h4 className="font-mono font-semibold mb-4 text-sm text-[var(--text-muted)] uppercase tracking-wider">
              Contact
            </h4>
            <a 
              href={`mailto:${companyInfo.email}`}
              className="text-[var(--accent-primary)] hover:underline text-sm font-mono"
            >
              {companyInfo.email}
            </a>
            
            {/* Social Links (placeholder) */}
            <div className="flex gap-4 mt-6">
              <a 
                href="#" 
                className="w-10 h-10 border border-[var(--border-subtle)] flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--accent-primary)] hover:border-[var(--accent-primary)] transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin size={18} />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 border border-[var(--border-subtle)] flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--accent-primary)] hover:border-[var(--accent-primary)] transition-colors"
                aria-label="Twitter"
              >
                <Twitter size={18} />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 border border-[var(--border-subtle)] flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--accent-primary)] hover:border-[var(--accent-primary)] transition-colors"
                aria-label="GitHub"
              >
                <Github size={18} />
              </a>
            </div>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center pt-8 border-t border-[var(--border-subtle)]">
          <p className="text-sm text-[var(--text-muted)]">
            {footerContent.copyright}
          </p>
          
          <div className="flex items-center gap-6 mt-4 md:mt-0">
            {footerContent.links.map((link) => (
              <a 
                key={link.label}
                href={link.href}
                className="text-sm text-[var(--text-muted)] hover:text-[var(--accent-primary)] transition-colors"
              >
                {link.label}
              </a>
            ))}
            
            <button 
              onClick={scrollToTop}
              className="w-10 h-10 border border-[var(--border-subtle)] flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--accent-primary)] hover:border-[var(--accent-primary)] transition-colors"
              aria-label="Back to top"
            >
              <ArrowUp size={18} />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
